import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { ReceiptText } from 'lucide-react';
import { EntityIndexLayout, WorkspaceShell } from '@/components/layouts';
import { SelectionPlaceholder } from '@/components/molecules';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { ReceiptListPanel } from '@/components/organisms/receipts/ReceiptListPanel';
import { ReceiptTableView } from '@/components/organisms/receipts/ReceiptTableView';
import {
  DEFAULT_RECEIPT_LIST_FILTERS,
  RECEIPT_KIND_FILTERS,
  parseReceiptDeliveryFilter,
  type ReceiptListFilters,
} from '@/components/organisms/receipts/receipt-list-filters';

const NUMBER_FILTER_OPS = ['eq', 'gt', 'lt', 'gte', 'lte'] as const;

export const Route = createFileRoute('/_dashboard/dashboard/receipts')({
  validateSearch: (search: Record<string, unknown>): ReceiptListFilters => {
    const sort = Number(search.sort);
    const page = Number(search.page);
    return {
      q: typeof search.q === 'string' ? search.q : DEFAULT_RECEIPT_LIST_FILTERS.q,
      kind: RECEIPT_KIND_FILTERS.some((filter) => filter.value === search.kind)
        ? (search.kind as ReceiptListFilters['kind'])
        : DEFAULT_RECEIPT_LIST_FILTERS.kind,
      delivery: parseReceiptDeliveryFilter(search.delivery),
      receiptNumber:
        typeof search.receiptNumber === 'string'
          ? search.receiptNumber
          : DEFAULT_RECEIPT_LIST_FILTERS.receiptNumber,
      invoiceNumber:
        typeof search.invoiceNumber === 'string'
          ? search.invoiceNumber
          : DEFAULT_RECEIPT_LIST_FILTERS.invoiceNumber,
      method:
        typeof search.method === 'string' ? search.method : DEFAULT_RECEIPT_LIST_FILTERS.method,
      totalOp: NUMBER_FILTER_OPS.includes(search.totalOp as ReceiptListFilters['totalOp'])
        ? (search.totalOp as ReceiptListFilters['totalOp'])
        : DEFAULT_RECEIPT_LIST_FILTERS.totalOp,
      totalValue:
        typeof search.totalValue === 'string'
          ? search.totalValue
          : DEFAULT_RECEIPT_LIST_FILTERS.totalValue,
      sort:
        Number.isFinite(sort) && sort >= 0
          ? Math.min(Math.floor(sort), 5)
          : DEFAULT_RECEIPT_LIST_FILTERS.sort,
      page:
        Number.isFinite(page) && page > 0 ? Math.floor(page) : DEFAULT_RECEIPT_LIST_FILTERS.page,
      dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
      dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    };
  },
  component: ReceiptsLayout,
});

function cleanReceiptSearch(next: Record<string, unknown>): ReceiptListFilters {
  if (!next.q) delete next.q;
  if (next.kind === DEFAULT_RECEIPT_LIST_FILTERS.kind) delete next.kind;
  if (next.delivery === DEFAULT_RECEIPT_LIST_FILTERS.delivery) delete next.delivery;
  if (!next.receiptNumber) delete next.receiptNumber;
  if (!next.invoiceNumber) delete next.invoiceNumber;
  if (!next.method) delete next.method;
  if (next.totalOp === DEFAULT_RECEIPT_LIST_FILTERS.totalOp) delete next.totalOp;
  if (!next.totalValue) delete next.totalValue;
  if (next.sort === DEFAULT_RECEIPT_LIST_FILTERS.sort) delete next.sort;
  if (next.page === DEFAULT_RECEIPT_LIST_FILTERS.page) delete next.page;
  if (!next.dateFrom) delete next.dateFrom;
  if (!next.dateTo) delete next.dateTo;
  return next as unknown as ReceiptListFilters;
}

function ReceiptsLayout() {
  const { location } = useRouterState();
  const router = useRouter();
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const filters = Route.useSearch();

  const idMatch = location.pathname.match(/\/dashboard\/receipts\/([^/]+)/);
  const selectedId = idMatch?.[1];
  const hasChildRoute = Boolean(selectedId);
  const isIndexRoute = /^\/dashboard\/receipts\/?$/.test(location.pathname);

  function handleFiltersChange(patch: Partial<ReceiptListFilters>) {
    void router.navigate({
      to: '/dashboard/receipts',
      search: cleanReceiptSearch({ ...filters, ...patch } as Record<string, unknown>),
      replace: true,
    });
  }

  if (pricingHidden || paymentInfoHidden) {
    return (
      <WorkspaceShell>
        <SelectionPlaceholder
          icon={ReceiptText}
          title="Receipt details are hidden for your role"
          iconClassName="h-10 w-10 opacity-30"
          className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground"
        />
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell>
      <EntityIndexLayout
        title="Receipts"
        storageKey="receipts-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() => void router.navigate({ to: '/dashboard/receipts', search: filters })}
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-[760px] sm:max-w-[760px]"
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitList={() => (
          <div className="flex h-full flex-col">
            <ReceiptListPanel
              selectedId={selectedId}
              onSelect={(receipt) =>
                void router.navigate({
                  from: Route.fullPath,
                  to: '/dashboard/receipts/$id',
                  params: { id: receipt.id },
                  // The URL's search, not this render's `filters`: while a filter
                  // change is still loading, `filters` holds the previous values.
                  search: (prev) => prev,
                })
              }
              compact
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <ReceiptTableView
            delivery={filters.delivery}
            onDeliveryChange={(delivery) => handleFiltersChange({ delivery, page: 1 })}
            onOpen={(id) =>
              openInSheet(
                () =>
                  void router.navigate({
                    to: '/dashboard/receipts/$id',
                    params: { id },
                    search: filters,
                  }),
              )
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex h-full flex-col md:hidden">
            {isIndexRoute ? (
              <ReceiptListPanel
                selectedId={selectedId}
                onSelect={(receipt) =>
                  void router.navigate({
                    to: '/dashboard/receipts/$id',
                    params: { id: receipt.id },
                    search: filters,
                  })
                }
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />
    </WorkspaceShell>
  );
}
